import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { Save, Mail, MessageSquare, Image as ImageIcon, Palette } from "lucide-react";

const TABS = [
  { id: "identity", label: "Identity", icon: ImageIcon },
  { id: "colors", label: "Colors", icon: Palette },
  { id: "email", label: "Email templates", icon: Mail },
  { id: "sms", label: "SMS templates", icon: MessageSquare },
];

export default function BrandSettings() {
  const { activeTenant, refreshTenants } = useTenant();
  const [tenant, setTenant] = useState(null);
  const [tab, setTab] = useState("identity");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!activeTenant) return;
    api.get(`/tenants/${activeTenant.id}`).then(({ data }) => setTenant(data));
  }, [activeTenant?.id]);  // eslint-disable-line

  const update = (patch) => setTenant((t) => ({ ...t, ...patch }));

  const save = async () => {
    const payload = {
      name: tenant.name,
      domain: tenant.domain,
      logo_url: tenant.logo_url,
      primary_color: tenant.primary_color,
      secondary_color: tenant.secondary_color,
      tagline: tenant.tagline,
      description: tenant.description,
      notification_emails: tenant.notification_emails || [],
      notification_phone: tenant.notification_phone,
      twilio_phone_number: tenant.twilio_phone_number,
      email_templates: tenant.email_templates || {},
      sms_templates: tenant.sms_templates || {},
    };
    await api.patch(`/tenants/${activeTenant.id}`, payload);
    setSaved(true);
    setTimeout(() => setSaved(false), 1800);
    refreshTenants();
  };

  if (!tenant) return <div className="p-10 text-[var(--n-text-muted)]">Loading…</div>;

  return (
    <div className="p-8 max-w-5xl" data-testid="brand-settings-page">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="n-label">Workspace · {tenant.name}</div>
          <h1 className="font-cab font-black text-4xl tracking-tight mt-1">Brand & templates</h1>
        </div>
        <button onClick={save} className="n-btn-blue n-btn flex items-center gap-2" data-testid="brand-save-button">
          <Save size={14} /> {saved ? "SAVED ✓" : "SAVE"}
        </button>
      </div>

      <div className="flex border-b border-[var(--n-border)] mb-6">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-cab font-medium border-b-2 -mb-px transition-colors duration-200 ${
              tab === id ? "border-white text-white" : "border-transparent text-[var(--n-text-sec)] hover:text-white"
            }`}
            data-testid={`brand-tab-${id}`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "identity" && <Identity tenant={tenant} update={update} />}
      {tab === "colors" && <Colors tenant={tenant} update={update} />}
      {tab === "email" && <EmailTpls tenant={tenant} update={update} />}
      {tab === "sms" && <SmsTpls tenant={tenant} update={update} />}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-4">
      <label className="n-label block mb-1">{label}</label>
      {children}
      {hint && <div className="text-[10px] font-mono-plex text-[var(--n-text-muted)] mt-1">{hint}</div>}
    </div>
  );
}

function Identity({ tenant, update }) {
  return (
    <div className="n-card p-6 grid md:grid-cols-2 gap-6">
      <div>
        <Field label="Brand name"><input className="n-input" value={tenant.name || ""} onChange={(e) => update({ name: e.target.value })} data-testid="brand-field-name" /></Field>
        <Field label="Tagline"><input className="n-input" value={tenant.tagline || ""} onChange={(e) => update({ tagline: e.target.value })} data-testid="brand-field-tagline" /></Field>
        <Field label="Domain" hint="e.g. yourdomain.com (used for public site)">
          <input className="n-input" value={tenant.domain || ""} onChange={(e) => update({ domain: e.target.value })} data-testid="brand-field-domain" />
        </Field>
        <Field label="Logo URL" hint="https URL to your logo (transparent PNG/SVG recommended)">
          <input className="n-input" value={tenant.logo_url || ""} onChange={(e) => update({ logo_url: e.target.value })} data-testid="brand-field-logo" />
        </Field>
        <Field label="Description">
          <textarea rows={4} className="n-input" value={tenant.description || ""} onChange={(e) => update({ description: e.target.value })} data-testid="brand-field-description" />
        </Field>
      </div>
      <div>
        <Field label="Notification emails" hint="comma-separated; receive emails on new leads">
          <input
            className="n-input"
            value={(tenant.notification_emails || []).join(", ")}
            onChange={(e) => update({ notification_emails: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
            data-testid="brand-field-notify-emails"
          />
        </Field>
        <Field label="Notification phone" hint="E.164 (+15551234567) — receives SMS on new leads">
          <input className="n-input" value={tenant.notification_phone || ""} onChange={(e) => update({ notification_phone: e.target.value })} data-testid="brand-field-notify-phone" />
        </Field>
        <Field label="Twilio phone number (override)" hint="Optional. If empty, platform default is used.">
          <input className="n-input" value={tenant.twilio_phone_number || ""} onChange={(e) => update({ twilio_phone_number: e.target.value })} data-testid="brand-field-twilio-phone" />
        </Field>

        <div className="border border-[var(--n-border)] p-4 mt-4">
          <div className="n-label mb-2">Preview</div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center font-cab font-black text-2xl" style={{ background: tenant.primary_color, color: "#fff" }}>
              {tenant.name?.[0] || "•"}
            </div>
            <div>
              <div className="font-cab font-black text-xl">{tenant.name}</div>
              <div className="text-xs text-[var(--n-text-sec)]">{tenant.tagline}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Colors({ tenant, update }) {
  return (
    <div className="n-card p-6 grid md:grid-cols-2 gap-6">
      {[
        ["primary_color", "Primary"],
        ["secondary_color", "Secondary"],
      ].map(([key, label]) => (
        <div key={key}>
          <Field label={`${label} color`}>
            <div className="flex items-center gap-3">
              <input type="color" value={tenant[key] || "#000000"} onChange={(e) => update({ [key]: e.target.value })} className="w-16 h-16 bg-transparent border border-[var(--n-border)]" data-testid={`brand-${key}-picker`} />
              <input className="n-input font-mono-plex text-xs" value={tenant[key] || ""} onChange={(e) => update({ [key]: e.target.value })} data-testid={`brand-${key}-hex`} />
            </div>
          </Field>
          <div className="mt-3 h-20 rounded" style={{ background: tenant[key] }} />
        </div>
      ))}
    </div>
  );
}

function EmailTpls({ tenant, update }) {
  const tpls = tenant.email_templates || {};
  const setTpl = (k, field, v) => update({ email_templates: { ...tpls, [k]: { ...(tpls[k] || {}), [field]: v } } });
  const keys = Object.keys(tpls).length ? Object.keys(tpls) : ["lead_notification", "welcome"];

  return (
    <div className="space-y-4">
      <Vars />
      {keys.map((k) => (
        <div key={k} className="n-card p-5" data-testid={`email-tpl-${k}`}>
          <div className="font-cab font-bold text-lg mb-3">{k.replace("_", " ")}</div>
          <Field label="Subject">
            <input className="n-input" value={tpls[k]?.subject || ""} onChange={(e) => setTpl(k, "subject", e.target.value)} data-testid={`email-tpl-${k}-subject`} />
          </Field>
          <Field label="Body">
            <textarea rows={6} className="n-input font-mono-plex text-xs" value={tpls[k]?.body || ""} onChange={(e) => setTpl(k, "body", e.target.value)} data-testid={`email-tpl-${k}-body`} />
          </Field>
        </div>
      ))}
    </div>
  );
}

function SmsTpls({ tenant, update }) {
  const tpls = tenant.sms_templates || {};
  const setTpl = (k, v) => update({ sms_templates: { ...tpls, [k]: { ...(tpls[k] || {}), body: v } } });
  const keys = Object.keys(tpls).length ? Object.keys(tpls) : ["lead_notification", "follow_up"];

  return (
    <div className="space-y-4">
      <Vars sms />
      {keys.map((k) => (
        <div key={k} className="n-card p-5" data-testid={`sms-tpl-${k}`}>
          <div className="font-cab font-bold text-lg mb-3">{k.replace("_", " ")}</div>
          <Field label="Message (160 chars recommended)">
            <textarea rows={3} className="n-input font-mono-plex text-xs" value={tpls[k]?.body || ""} onChange={(e) => setTpl(k, e.target.value)} data-testid={`sms-tpl-${k}-body`} />
          </Field>
          <div className="text-[10px] font-mono-plex text-[var(--n-text-muted)]">
            {(tpls[k]?.body || "").length} characters
          </div>
        </div>
      ))}
    </div>
  );
}

function Vars({ sms }) {
  return (
    <div className="n-card p-4 border-dashed">
      <div className="n-label mb-2">Variables you can use</div>
      <div className="flex flex-wrap gap-2 font-mono-plex text-[11px]">
        {["{{tenant_name}}", "{{lead_name}}", "{{lead_email}}", "{{lead_phone}}", sms ? null : "{{lead_message}}"].filter(Boolean).map((v) => (
          <span key={v} className="n-chip">{v}</span>
        ))}
      </div>
    </div>
  );
}
